const logger = require('nordic/logger')('LoginBuilder');

const apiService = require('../service/api');
const {
  SUBMIT_FORM_ID,
  PLATFORM,
  URLS: { BASE_URL_MERCADO_LIBRE },
  NATIVE_APP_NAME_MAP,
  LOGIN_TYPE,
  HIDDEN_COMPONENTS,
} = require('../../constants/app');
const internalConfigurationsEnabled = require('./internalConfigurations');
const { createInternalWebviewDeeplink, buildSniffingWebviewParams } = require('./deeplinks');

class LoginBuilder {
  /**
  * Gets the encoded callback URL based on configuration parameters.
  * If enableMlTheme is true, it builds a login URL with the provided parameters and encodes it.
  * Otherwise, it simply encodes the provided callback URL.
  *
  * @param {Object} params - The parameters object
  * @param {string} params.callbackUrl - The callback URL to be encoded
  * @param {boolean} params.enableMlTheme - Flag to enable Mercado Libre theme
  * @param {string} params.siteId - The site identifier
  * @param {string} params.loginType - The type of login (checkout_pro or scp_checkout_off)
  * @returns {string} The encoded callback URL
  */
  static getCallbackUrl({ callbackUrl, enableMlTheme, siteId, loginType }) {
    if (enableMlTheme) {
      return LoginBuilder.buildLoginURL(siteId, PLATFORM.MP, encodeURIComponent(callbackUrl), loginType);
    }

    return callbackUrl;
  }

  static sendWarningLog(message, params) {
    const errorFromServer = typeof window !== 'undefined' && typeof document !== 'undefined';

    if (errorFromServer) {
      apiService.trackWarn(message);
      return;
    }

    logger.warn(message, params);
  }

  /**
   * Generate the login URL (MercadoLibre)
   * @param configurations
   * @param callbackUrl - url string without being encoded
   * @returns {string}
   */
  static generateLoginURL(configurations = {}, callbackUrl) {
    const platform = configurations.platform || {};
    const siteId = (platform.siteId || '').toLowerCase();
    const { enableMlTheme } = internalConfigurationsEnabled(configurations.internalConfigurations);
    const platformId = enableMlTheme ? PLATFORM.ML : (platform.id || '').toLowerCase();
    const loginType = configurations.loginType || LOGIN_TYPE.STANDARD;
    const { flowId } = configurations;

    return LoginBuilder.buildLoginURL(
      siteId,
      platformId,
      LoginBuilder.getCallbackUrl({ callbackUrl, enableMlTheme, siteId, loginType }),
      loginType,
      flowId,
    );
  }

  /**
   * Builds a MercadoLibre login URL with the provided parameters
   *
   * @param {string} siteId - The site identifier
   * @param {string} platformId - The platform identifier (e.g., 'mp' for MercadoPago)
   * @param {string} callback - The callback URL to be encoded once
   * @param {string} loginType - The type of login (checkout_pro or scp_checkout_off)
   * @param {string} flowId - The flow identifier for tracking the user journey
   * @returns {string} The complete login URL as a string
   */
  static buildLoginURL(siteId, platformId, callback, loginType, flowId) {
    const requiredParams = { siteId, platformId, callback, loginType, flowId };

    Object.entries(requiredParams).forEach(([key, value]) => {
      if (!value) {
        LoginBuilder.sendWarningLog(`buildLoginURL: ${key} is required`, requiredParams);
      }
    });

    const loginUrl = new URL(`${BASE_URL_MERCADO_LIBRE}/jms/${siteId}/lgz/login`);
    loginUrl.searchParams.set('platform_id', platformId);
    loginUrl.searchParams.set('go', callback);
    loginUrl.searchParams.set('loginType', loginType);
    loginUrl.searchParams.set('journey_id', flowId);

    return loginUrl.toString();
  }

  /**
   * Checks if a user is currently logged in by making an API call
   * @async
   * @returns {Promise<boolean>} A promise that resolves to true if user is logged in, false otherwise
   */
  static async isUserLogged() {
    return apiService.isUserLogged().then((response) => !!response.logged);
  }

  /**
   * ChallengeConfig function that builds login challenge configuration
   * @param challengeRawData - component sent by Flows, this may be used to get some extra data.
   * @param configuration - Frontend's environment configurations.
   * @param callbackUrl - desired url to use a return url from challenge, without being encoded
   */
  static async loginConfig(challengeRawData, configurations, callbackUrl, components = [], hidden_components) {
    const { flowId } = challengeRawData;
    const loginType = hidden_components?.find((component) => component.id === HIDDEN_COMPONENTS.LOGIN_TYPE)?.value;
    // TODO validate nativeAppName
    let challengeUrl = LoginBuilder.generateLoginURL({...configurations, loginType, flowId}, callbackUrl);

    if (configurations.isWebview) {
      const params = buildSniffingWebviewParams({ authRequired: true });
      const checkoutURL = new URL(window.location.href);
      challengeUrl = createInternalWebviewDeeplink(
        checkoutURL.href,
        params,
        configurations.nativeApp?.name ?? NATIVE_APP_NAME_MAP.MercadoLibre,
      );
    }

    return Promise.resolve({
      challengeUrl,
      pollingFunction: LoginBuilder.isUserLogged,
      submitFormId: SUBMIT_FORM_ID.LOGIN_FORM,
      popupSetup: {
        width: 500,
        height: 600,
      },
    });
  }

  /**
   * ChallengeConfig function that builds login challenge remedy configuration
   * @param challengeRawData - component sent by Flows, this may be used to get some extra data.
   * @param configurations - Frontend's environment configurations.
   * @param redirectUrl - desired url to use a return url from challenge
   * @param components - step components
   * @param hiddenComponents - step hidden_components
   */
  static async loginChallengeRemedyConfig(challengeRawData, configurations, redirectUrl, components, hiddenComponents) {
    const challengeURL = hiddenComponents.find(({ id }) => id === 'redirect_to');

    return Promise.resolve({
      challengeUrl: challengeURL?.value,
      submitFormId: SUBMIT_FORM_ID.LOGIN_REMEDY_FORM,
      popupSetup: {
        width: 500,
        height: 600,
        completeOnPostmessage: true,
      },
    });
  }
}

/**
 * Expose generateLoginURL method so it can be use outside without initializing the LoginBuilder class
 */
module.exports.generateWaitingPage = LoginBuilder.generateWaitingPage;
module.exports.generateLoginURL = LoginBuilder.generateLoginURL;
module.exports.isUserLogged = LoginBuilder.isUserLogged;
module.exports.loginConfig = LoginBuilder.loginConfig;
module.exports.loginChallengeRemedyConfig = LoginBuilder.loginChallengeRemedyConfig;
module.exports.buildLoginURL = LoginBuilder.buildLoginURL;
module.exports.getCallbackUrl = LoginBuilder.getCallbackUrl;
module.exports.sendWarningLog = LoginBuilder.sendWarningLog;
